import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Briefcase, MapPin, Calendar, Trash2, Loader2, Search, X,
    Pencil, RotateCcw, Users, Plus,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import TopNav from "../components/TopNav";
import DocCountBadge from "../components/DocCountBadge";
import NewProjectDialog from "../components/NewProjectDialog";
import EditProjectDialog from "../components/EditProjectDialog";
import SaveToWorkgroupDialog from "../components/SaveToWorkgroupDialog";
import {
    listProjects, createProject, deleteProject, updateProject,
    resetProject, getUploadCounts, listActiveProjectJobs,
} from "../lib/api";

function formatDate(iso) {
    if (!iso) return "";
    // Accept both "YYYY-MM-DD" date strings AND full ISO timestamps
    // (the email-scan pipeline can write either). Anything else falls
    // back to the original value — never produces a mangled split.
    const datePart = String(iso).slice(0, 10);
    const [y, m, d] = datePart.split("-");
    if (!y || !m || !d || y.length !== 4) return iso;
    return `${m}/${d}/${y}`;
}

/**
 * Full-page Projects list — replaces the popup variant on mobile so it
 * matches Contacts / Leads / Templates etc. Search field intentionally
 * does NOT auto-focus (was popping up the keyboard the moment users
 * tapped "Projects" on a phone).
 */
export default function ProjectsPage() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadCounts, setUploadCounts] = useState({});
    const [query, setQuery] = useState("");
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [shareProject, setShareProject] = useState(null);
    // iter-108: live progress for projects whose auto-quote is still
    // running. Polled every 5s; we always re-fetch the project list too
    // so a freshly auto-created (email-scan) project surfaces ASAP.
    const [activeJobs, setActiveJobs] = useState({}); // {project_id: job}

    const fetchActiveJobs = async () => {
        try {
            const rows = await listActiveProjectJobs();
            const map = {};
            for (const r of rows) map[r.project_id] = r;
            setActiveJobs(map);
            return rows;
        } catch {
            return [];
        }
    };

    useEffect(() => {
        // Initial load already fetches projects; start a 5s tick for
        // progress + refresh the project list when an active job clears
        // (or arrives — covers the "new email-scan project appears"
        // case without manual refresh).
        let prevActive = -1;
        const tick = async () => {
            const rows = await fetchActiveJobs();
            if (rows.length !== prevActive) {
                prevActive = rows.length;
                try {
                    const p = await listProjects();
                    setProjects(p);
                } catch { /* ignore */ }
            }
        };
        tick();
        const id = setInterval(tick, 5000);
        return () => clearInterval(id);
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [p, counts] = await Promise.all([
                listProjects(),
                getUploadCounts().catch(() => ({})),
            ]);
            setProjects(p);
            setUploadCounts(counts || {});
        } catch {
            toast.error("Couldn't load projects");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { fetchAll(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return projects;
        return projects.filter((p) => {
            const name = (p.name || "").toLowerCase();
            const jobId = (p.job_id || "").toLowerCase();
            const location = (p.location || "").toLowerCase();
            return name.includes(q) || jobId.includes(q) || location.includes(q);
        });
    }, [projects, query]);

    const handleCreate = async ({ name, location, job_id }) => {
        try {
            const created = await createProject({ name, location, job_id });
            setProjects((prev) => [created, ...prev]);
            toast.success(`Project "${created.name}" created`);
            navigate(`/workspace/${created.id}`);
        } catch (e) {
            toast.error("Failed to create project");
            throw e;
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await deleteProject(pendingDelete.id);
            setProjects((prev) => prev.filter((p) => p.id !== pendingDelete.id));
            toast.success(`Deleted "${pendingDelete.name}"`);
            setPendingDelete(null);
        } catch {
            toast.error("Couldn't delete project");
        } finally {
            setDeleting(false);
        }
    };

    const handleReset = async (project) => {
        const ok = window.confirm(
            `Reset "${project.name}"?\n\n` +
            "• All AI takeoff drawings on this project will be DELETED.\n" +
            "• Uploads, page splits, analyses, and estimates are preserved."
        );
        if (!ok) return;
        try {
            await resetProject(project.id);
            toast.success(`Takeoffs reset for "${project.name}"`);
        } catch {
            toast.error("Couldn't reset takeoffs");
        }
    };

    const handleUpdate = async (id, patch) => {
        try {
            const updated = await updateProject(id, patch);
            setProjects((prev) => prev.map((p) => (p.id === id ? updated : p)));
            toast.success("Project updated");
        } catch {
            toast.error("Couldn't update project");
            throw new Error("update failed");
        }
    };

    return (
        <div className="h-dvh bg-[#0A1128] text-white flex flex-col" data-testid="projects-page">
            <TopNav />
            <main className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6 pb-40 max-w-5xl w-full mx-auto">
                {/* Header */}
                <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold flex items-center gap-2">
                            <Briefcase className="w-3 h-3 text-[#00A3FF]" />
                            Workspace
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                            All Projects
                            <span className="ml-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 align-middle">
                                {query ? `${filtered.length} / ${projects.length}` : `${projects.length} total`}
                            </span>
                        </h1>
                        <p className="text-sm text-slate-400 mt-1">
                            Every project saved to your Steeltooth workspace.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setNewOpen(true)}
                        data-testid="btn-new-project"
                        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/25 text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        New project
                    </button>
                </header>

                {/* Search — explicitly NOT auto-focused, otherwise the mobile keyboard
                    pops up the moment the page opens. */}
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <Input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name / address / id"
                        data-testid="input-projects-search"
                        autoFocus={false}
                        className="bg-[#0E1A3A] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] pl-9 pr-9 h-11"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            data-testid="btn-clear-projects-search"
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-[#1C253C] transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* List */}
                <ul className="space-y-2" data-testid="projects-list">
                    {loading && (
                        <li className="text-sm text-slate-400 p-6 text-center flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
                        </li>
                    )}
                    {!loading && projects.length === 0 && (
                        <li className="text-sm text-slate-400 p-6 text-center" data-testid="projects-empty">
                            No projects yet. Tap “New project” to create one.
                        </li>
                    )}
                    {!loading && projects.length > 0 && filtered.length === 0 && (
                        <li className="text-sm text-slate-400 p-6 text-center" data-testid="projects-search-empty">
                            No projects match <span className="text-white font-semibold">&ldquo;{query}&rdquo;</span>.
                        </li>
                    )}
                    {filtered.map((p) => {
                        const job = activeJobs[p.id];
                        const isProcessing = !!job;
                        const pct = Math.max(2, Math.min(100, job?.progress_pct || 5));
                        return (
                        <li
                            key={p.id}
                            data-testid={`projects-item-${p.id}`}
                            className={`flex flex-col gap-2 px-3 py-3 bg-[#131A2F] border rounded-md min-w-0 transition-colors cursor-pointer ${isProcessing ? "border-[#00A3FF]/60 ring-1 ring-[#00A3FF]/20" : "border-[#2A3655] hover:border-[#00A3FF]/60"}`}
                            onClick={() => navigate(`/workspace/${p.id}`)}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-9 h-9 rounded-sm bg-[#1C253C] border border-[#2A3655] text-[#00A3FF] flex items-center justify-center shrink-0">
                                    <Briefcase className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                        <div className="font-semibold text-white text-sm overflow-hidden text-ellipsis whitespace-nowrap" title={p.name}>
                                            {p.name}
                                        </div>
                                        {p.job_id && (
                                            <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#00A3FF]">
                                                {p.job_id}
                                            </span>
                                        )}
                                        {isProcessing && (
                                            <span
                                                data-testid={`projects-processing-badge-${p.id}`}
                                                className="shrink-0 inline-flex items-center gap-1 font-bold text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/50 text-[#00A3FF]"
                                            >
                                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                                Auto-quoting
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400 mt-0.5">
                                        <span className="flex items-center gap-1 min-w-0">
                                            <MapPin className="w-3 h-3 shrink-0" />
                                            <span className="truncate">{p.location}</span>
                                        </span>
                                        <span className="flex items-center gap-1 shrink-0">
                                            <Calendar className="w-3 h-3" />
                                            {formatDate(p.updated_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 justify-end sm:shrink-0 sm:ml-auto" onClick={(e) => e.stopPropagation()}>
                                <DocCountBadge count={uploadCounts[p.id] || 0} testId={`projects-doc-count-${p.id}`} />
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setEditingProject(p); }}
                                    data-testid={`btn-edit-project-${p.id}`}
                                    aria-label={`Edit ${p.name}`}
                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#007AFF]/10 border border-transparent hover:border-[#00A3FF]/30 transition-colors active:scale-95"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setShareProject(p); }}
                                    data-testid={`btn-share-wg-project-${p.id}`}
                                    aria-label={`Share ${p.name} to workgroup`}
                                    title="Share to workgroup"
                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 border border-transparent hover:border-emerald-500/30 transition-colors active:scale-95"
                                >
                                    <Users className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleReset(p); }}
                                    data-testid={`btn-reset-project-${p.id}`}
                                    aria-label={`Reset ${p.name} takeoffs`}
                                    title="Reset takeoffs — wipe AI shapes, keep plans/analyses"
                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/30 transition-colors active:scale-95"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setPendingDelete(p); }}
                                    data-testid={`btn-delete-project-${p.id}`}
                                    aria-label={`Delete ${p.name}`}
                                    className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors active:scale-95"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            </div>
                            {isProcessing && (
                                <div className="mt-1" data-testid={`projects-progress-${p.id}`}>
                                    <div className="h-1.5 w-full rounded-full bg-[#1C253C] overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-[#00A3FF] to-[#7B5BFF] transition-all duration-700"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-slate-400">
                                        <span className="truncate">{job?.stage || "Auto-quoting from email…"}</span>
                                        <span className="font-mono text-[#00A3FF] tabular-nums">{pct}%</span>
                                    </div>
                                </div>
                            )}
                        </li>
                        );
                    })}
                </ul>
            </main>

            <NewProjectDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                onCreate={handleCreate}
            />
            <EditProjectDialog
                open={!!editingProject}
                project={editingProject}
                onOpenChange={(o) => !o && setEditingProject(null)}
                onSave={(id, patch) => handleUpdate(id, patch)}
            />
            <SaveToWorkgroupDialog
                kind="project"
                itemId={shareProject?.id || null}
                open={!!shareProject}
                onOpenChange={(o) => !o && setShareProject(null)}
            />

            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
            >
                <AlertDialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[440px]"
                    data-testid="delete-project-confirm"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete this project?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            <span className="text-white font-semibold">{pendingDelete?.name}</span>
                            {pendingDelete?.job_id ? (
                                <>{" "}(<span className="font-mono text-[#00A3FF]">{pendingDelete.job_id}</span>)</>
                            ) : null}
                            {" "}will be permanently removed from your workspace. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-cancel-delete-project"
                            className="bg-[#1C253C] border-[#2A3655] text-slate-300 hover:bg-[#2A3655] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete-project"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deleting ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                            ) : (
                                <><Trash2 className="w-4 h-4 mr-2" />Delete</>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
